import React, { useState } from 'react';
import FloatingActionButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/FloatingActionButton';
import Menu from 'housecall-ui/dist/DesignSystem/Navigation/Menus/Menu';
import MenuItem from 'housecall-ui/dist/DesignSystem/Navigation/Menus/MenuItem';
import useMediaQuery from 'housecall-ui/dist/DesignSystem/Styles/useMediaQuery';
import useTheme from 'housecall-ui/dist/DesignSystem/Styles/useTheme';
import AddIcon from 'housecall-ui/dist/IconsAndImages/Icons/Add';
import PropTypes from 'prop-types';

import TrackedButton from 'components/Common/Inputs/Buttons/TrackedButton';
import TrackedMenuItem from 'components/Common/Navigation/Menus/TrackedMenuItem';
import RedirectTo from 'components/Common/RedirectTo';
import {
  customerIntakeFlowStates,
  useIsIntakeFlowEnabled,
} from 'components/CustomerIntakeOnboarding/useIntakeFlowVisiblePage';
import { leadsEnabledState, useIsLeadsFeatureEnabled } from 'components/Leads/hooks/useIsLeadsFeatureEnabled';
import AddEstimateDialog from 'components/Toolbar/AddEstimateDialog';
import url from 'components/url';

import { HEADER } from '../../Services/Analytics/TrackEventsService';
import NewCustomerDialogContainer from '../Customer/NewCustomerDialogContainer';

const AddNew = ({ canAddJob, jobsFTU, showAddProposal, canUseVirtualEstimates, isConnectedToOnCallAir }) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const [menuState, setMenuState] = useState({ show: false, anchorEl: null });
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [virtualEstimateDialogOpen, setVirtualEstimateDialogOpen] = useState(false);
  const intakeStatus = useIsIntakeFlowEnabled();
  const isLeadsEnabled = useIsLeadsFeatureEnabled() === leadsEnabledState.enabled;
  const customerIntakeEnabled = intakeStatus === customerIntakeFlowStates.ENABLED;

  const handleOnAddClick = (event) => {
    event.preventDefault();
    setMenuState({ show: true, anchorEl: event.currentTarget });
  };

  const handleCloseAddMenu = () =>
    new Promise((resolve) => {
      resolve(setMenuState({ show: false, anchorEl: null }));
    });

  const trackingProperties = { button_location: HEADER };

  const handleJobClick = () => {
    if (!canAddJob) return RedirectTo.billingWithPaywall();
    if (jobsFTU) return RedirectTo.scheduleAndDispatch();
    return RedirectTo.addJob();
  };

  const maybeShowVirtualEstimateDialog = () => {
    if (canUseVirtualEstimates) {
      handleCloseAddMenu().then(() => {
        setVirtualEstimateDialogOpen(true);
      });
    } else {
      RedirectTo.addEstimate();
    }
  };

  const handleOnAddEstimate = () => {
    if (canAddJob) return maybeShowVirtualEstimateDialog();
    return RedirectTo.billingWithPaywall();
  };

  const handleOnAddEvent = () => {
    if (canAddJob) return RedirectTo.addEvent();
    return RedirectTo.billingWithPaywall();
  };

  const handleOnAddCustomer = () => {
    handleCloseAddMenu().then(() => {
      setCustomerDialogOpen(true);
    });
  };

  const handleProposalClick = () => {
    RedirectTo.addProposal();
  };

  const FloatIconProps = {
    buttonComponent: FloatingActionButton,
    variant: 'round',
    size: 'small',
    children: <AddIcon />,
  };
  const ButtonProps = {
    variant: 'contained',
    children: 'New',
  };

  return (
    <>
      <TrackedButton
        trackingName="new"
        trackingProperties={trackingProperties}
        onClick={(event) => {
          handleOnAddClick(event);
        }}
        version="v2"
        color="primary"
        {...(smUp ? ButtonProps : FloatIconProps)}
      />

      <Menu
        open={menuState.show}
        anchorEl={menuState.anchorEl}
        disableScrollLock
        getContentAnchorEl={null}
        onClose={handleCloseAddMenu}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        version="v2"
      >
        <TrackedMenuItem
          trackingName="new job"
          trackingProperties={trackingProperties}
          onClick={handleJobClick}
          version="v2"
          data-dd-action-name="navbar-new-job-button"
        >
          Job
        </TrackedMenuItem>
        {showAddProposal && (
          <TrackedMenuItem
            trackingName="new proposal"
            trackingProperties={trackingProperties}
            onClick={handleProposalClick}
            version="v2"
          >
            Proposal
          </TrackedMenuItem>
        )}
        <TrackedMenuItem
          trackingName="new estimate"
          trackingProperties={trackingProperties}
          onClick={handleOnAddEstimate}
          version="v2"
        >
          Estimate
        </TrackedMenuItem>
        <TrackedMenuItem
          trackingName="new event"
          trackingProperties={trackingProperties}
          onClick={handleOnAddEvent}
          version="v2"
        >
          Event
        </TrackedMenuItem>
        <TrackedMenuItem
          trackingName="new customer"
          trackingProperties={trackingProperties}
          onClick={handleOnAddCustomer}
          version="v2"
        >
          Customer
        </TrackedMenuItem>
        {customerIntakeEnabled && (
          <MenuItem component="a" href={url.customerIntake.path} version="v2">
            Intake
          </MenuItem>
        )}
        {isConnectedToOnCallAir && (
          <MenuItem component="a" href="/pro/on_call_air/proposals/new" version="v2">
            OnCall Air proposal
          </MenuItem>
        )}
        {isLeadsEnabled && (
          <MenuItem component="a" href={url.leads.path} version="v2">
            Lead
          </MenuItem>
        )}
      </Menu>
      <NewCustomerDialogContainer
        open={customerDialogOpen}
        closeDialog={() => {
          setCustomerDialogOpen(false);
        }}
        onSuccess={(customer, newLocation) => {
          window.location = newLocation;
        }}
      />
      <AddEstimateDialog
        open={virtualEstimateDialogOpen}
        onClose={() => setVirtualEstimateDialogOpen(false)}
        handleChooseEstimateType={(isVirtual) => RedirectTo.addEstimate(isVirtual)}
      />
    </>
  );
};

AddNew.propTypes = {
  canAddJob: PropTypes.bool,
  jobsFTU: PropTypes.bool,
  showAddProposal: PropTypes.bool,
  canUseVirtualEstimates: PropTypes.bool,
  isConnectedToOnCallAir: PropTypes.bool,
};

AddNew.defaultProps = {
  jobsFTU: false,
};

export default AddNew;
